import { Authenticated, HttpError, Refine } from "@refinedev/core";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import { ThemedLayout } from "./components/themedLayout";
import { ThemedHeader } from "./components/themedLayout/header";
import { ErrorComponent } from "@refinedev/antd";
import "@refinedev/antd/dist/reset.css";
import routerBindings, {
  CatchAllNavigate,
  UnsavedChangesNotifier,
} from "@refinedev/react-router-v6";
import dataProvider from "@refinedev/simple-rest";
import * as Sentry from "@sentry/react";
import axios from "axios";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { RESOURCES } from "./App.constants";
import {
  makeAuthProvider,
  REFRESH_KEY,
  TOKEN_KEY,
} from "./components/providers/authProvider";
import { refreshSocketToken } from "./utils/socketManager";
import { smartNotificationProvider } from "./components/providers/smartNotification";
import { ColorModeContextProvider } from "./contexts/color-mode";
import { BankAccountsList } from "./pages/bank-accounts";
import { BanksList } from "./pages/banks";
import { CompanyEdit, CompanyList } from "./pages/company";
import { ConfigFees } from "./pages/config-fee";
import { ConfirmEmail } from "./pages/confirmEmail";
import { CryptoWithdrawalList } from "./pages/crypto-withdrawal";
import { CurrenciesList } from "./pages/currencies";
import { CustomerList } from "./pages/customers";
import { ForgotPassword } from "./pages/forgotPassword";
import { InvoicePayablesList } from "./pages/invoicePayables";
import { InvoiceReceivables } from "./pages/invoiceReceivables";
import { Login } from "./pages/login";
import { CategoryShow, PaymentOrderList, PaymentOrderCreatePage } from "./pages/payment-order";
import { BlogPostShow, PaymentRequestList, PaymentRequestCreatePage } from "./pages/payment-request";
import { Register } from "./pages/register";
import { ResetPassword } from "./pages/resetPassword";
import { TransactionsHistory } from "./pages/transactions-history";
import { Version } from "./pages/version";
import { Wallet } from "./pages/wallet/wallet";
import { WalletsList } from "./pages/wallets";
import { ExchangeList } from "./pages/exchange";
import { CustomerEdit } from "./pages/customers/edit";
import { BeneficiariesDetail } from "./pages/customers/edit/components/beneficiaries-detail";
import { BeneficiariesRemittersList } from "./pages/beneficiaries-remitters";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const ROOT_URL = process.env.REACT_APP_ROOT_URL || "";
export const SmartAuthProvider = makeAuthProvider(ROOT_URL);
export const TIMEOUT_PATH = "/app/login";

export const httpClient = axios.create({
  transformRequest: [
    function (data, headers) {
      if (headers) {
        headers["Authorization"] = `Bearer ${localStorage.getItem(TOKEN_KEY)}`;
      }
      return JSON.stringify(data);
    },
  ],
  headers: {
    "Content-Type": "application/json",
  },
});

// --- Variable using refresh ---
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

httpClient.interceptors.response.use(
  (response) => {
    const totalCount = response.headers["x-total-count"];
    return {
      ...response,
      totalCount: totalCount ? Number(totalCount) : undefined,
    };
  },
  async (error) => {
    const originalRequest = error.config as any;
    const customError: HttpError = {
      ...error,
      message: error.response?.data?.message,
      statusCode: error.response?.status,
      reason_code: error.response?.data?.reason_code,
    };
    if (!error.response || customError.statusCode !== 401) {
      return Promise.reject(customError);
    }

    if (isRefreshing) {
      return new Promise(function (resolve, reject) {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          if (!originalRequest.headers) originalRequest.headers = {};
          originalRequest.headers.Authorization = `Bearer ${token}`;
          // Parse string
          if (typeof originalRequest.data === "string") {
            try {
              originalRequest.data = JSON.parse(originalRequest.data);
            } catch (e) {
              console.error(e);
            }
          }
          return httpClient(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }
    // Flag refreshing
    isRefreshing = true;
    const refreshToken = localStorage.getItem(REFRESH_KEY);

    if (!refreshToken) {
      processQueue(null, null);
      isRefreshing = false;
      // No refresh token
      localStorage.clear();
      window.location.href = TIMEOUT_PATH;
      return Promise.reject(customError);
    }

    try {
      // Call API refresh
      const res = await axios.post(
        `${ROOT_URL}/api/v1/auth/refresh-token`,
        undefined,
        {
          headers: {
            "x-refresh-token": refreshToken,
          },
        }
      );
      const newAccessToken = res.data.data["access_token"];
      const newRefreshToken = res.data.data["refresh_token"];

      localStorage.setItem(TOKEN_KEY, newAccessToken);
      if (newRefreshToken) localStorage.setItem(REFRESH_KEY, newRefreshToken);

      // Update default header
      httpClient.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

      // Refresh socket token to update new token for socket connection
      refreshSocketToken(newAccessToken);

      // Call all API request in queue
      processQueue(null, newAccessToken);

      // Retry root request
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      // Parse string
      if (typeof originalRequest.data === "string") {
        try {
          originalRequest.data = JSON.parse(originalRequest.data);
        } catch (e) {
          console.error(e);
        }
      }
      return httpClient(originalRequest);
    } catch (err) {
      processQueue(err, null);
      // Refresh fail → logout
      localStorage.clear();
      window.location.href = TIMEOUT_PATH;
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

function App() {
  const InnerApp = () => {
    return (
      <SentryRoutes>
        <Route
          element={
            <Authenticated
              key="fallback_to_login"
              fallback={<CatchAllNavigate to="/login" />}
            >
              <ThemedLayout Header={ThemedHeader}>
                <Outlet />
              </ThemedLayout>
            </Authenticated>
          }
        >
          <Route index element={<Navigate to={"/payment-request"} />} />
          <Route path="/exchange">
            <Route index element={<ExchangeList />} />
          </Route>
          <Route path="/crypto-withdrawal">
            <Route index element={<CryptoWithdrawalList />} />
          </Route>
          <Route path="/payment-request">
            <Route index element={<PaymentRequestList />} />
            <Route path="create" element={<PaymentRequestCreatePage />} />
            <Route path="show/:id" element={<BlogPostShow />} />
          </Route>
          <Route path="/invoice-payables">
            <Route index element={<InvoicePayablesList />} />
          </Route>
          <Route path="/invoice-receivables">
            <Route index element={<InvoiceReceivables />} />
          </Route>
          <Route path="/payment-order">
            <Route index element={<PaymentOrderList />} />
            <Route path="show/:id" element={<CategoryShow />} />
          </Route>
          <Route path="/payment-order/create">
            <Route index element={<PaymentOrderCreatePage />} />
          </Route>
          <Route path="/company">
            <Route index element={<CompanyList />} />
            <Route path=":id" element={<CompanyEdit />} />
          </Route>
          <Route path="/bank-accounts">
            <Route index element={<BankAccountsList />} />
          </Route>
          <Route path="/wallets">
            <Route index element={<WalletsList />} />
          </Route>
          <Route path="/config-fees">
            <Route index element={<ConfigFees />} />
          </Route>
          <Route path="/version-review">
            <Route index element={<Version />} />
          </Route>
          <Route path="/banks">
            <Route index element={<BanksList />} />
          </Route>
          <Route path="/currencies">
            <Route index element={<CurrenciesList />} />
          </Route>
          <Route path="/customers">
            <Route index element={<CustomerList />} />
          </Route>
          <Route path="/customers/:id">
            <Route index element={<CustomerEdit />} />
            <Route path="beneficiaries" element={<BeneficiariesDetail />} />
          </Route>
          <Route path="/beneficiaries-remitters">
            <Route index element={<BeneficiariesRemittersList />} />
          </Route>
          <Route path="/beneficiaries-remitters/:id">
            <Route index element={<BeneficiariesDetail isBeneficiariesRemitters={true} />} />
          </Route>
          <Route path="/wallet">
            <Route index element={<Wallet />} />
          </Route>
          <Route path="/transactions-history">
            <Route index element={<TransactionsHistory />} />
          </Route>
        </Route>
        <Route
          element={
            <Authenticated key="fallback_to_outlet" fallback={<Outlet />}>
              <Navigate to="/payment-request" />
            </Authenticated>
          }
        >
          <Route path="/login" element={<Login />} />
          <Route path="/login/:displayMode" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
        </Route>
        <Route
          element={
            <Authenticated key="header">
              <ThemedLayout Header={ThemedHeader}>
                <Outlet />
              </ThemedLayout>
            </Authenticated>
          }
        >
          <Route path="*" element={<ErrorComponent />} />
        </Route>
      </SentryRoutes>
    );
  };

  return (
    <BrowserRouter basename={process.env.PUBLIC_URL}>
      <RefineKbarProvider>
        <ColorModeContextProvider>
          <Refine
            dataProvider={dataProvider(ROOT_URL, httpClient)}
            notificationProvider={smartNotificationProvider}
            routerProvider={routerBindings}
            authProvider={SmartAuthProvider}
            resources={RESOURCES}
            options={{
              breadcrumb: false,
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              reactQuery: {
                devtoolConfig: false,
              },
              disableTelemetry: true,
            }}
          >
            <InnerApp />
            <RefineKbar />
            <UnsavedChangesNotifier />
          </Refine>
        </ColorModeContextProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
}

export default App;
